import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  OutlinedInput,
  Typography,
  Alert,
  CircularProgress,
} from "@mui/material";

import {
  adicionarPapelExtra,
  removerPapelExtra,
  obterPapeisExtra,
} from "../../services/perfilService";

const PAPEL_OPTIONS = [
  { value: "revisor_projeto", label: "Revisor de Projeto" },
  { value: "chefe_departamento", label: "Chefe de Departamento" },
  { value: "arguente", label: "Arguente" },
  { value: "supervisor", label: "Supervisor" },
  { value: "presidente", label: "Presidente" },
];

const GerirPapeisUsuario = ({ usuario, onRolesUpdated }) => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState(false);

  // Debug: Logs iniciais
  console.log("=== GerirPapeisUsuario Debug ===");
  console.log("Usuario recebido:", usuario);
  console.log("Usuario ID:", usuario?.id);
  console.log("Usuario dadosAdicionais:", usuario?.dadosAdicionais);

  // Carregar papéis atuais do usuário
  useEffect(() => {
    const carregarPapeis = async () => {
      console.log("🔄 Iniciando carregamento de papéis para usuário:", usuario?.id);
      
      if (!usuario?.id) {
        console.error("❌ Usuário sem ID!");
        setError("Usuário inválido");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        console.log("📡 Fazendo chamada para obterPapeisExtra...");
        const response = await obterPapeisExtra(usuario.id);
        console.log("✅ Resposta do servidor:", response);
        
        const papeisBackend = response.rolesExtra || [];
        console.log("📋 Papéis do backend:", papeisBackend);
        
        setRoles(papeisBackend);
        console.log("✅ Estado atualizado com papéis:", papeisBackend);
        
      } catch (error) {
        console.error("❌ Erro ao carregar papéis:", error);
        console.log("🔄 Tentando usar dados locais como fallback...");
        
        // Fallback para dados locais
        const papeisFallback = usuario?.dadosAdicionais?.rolesExtra || [];
        console.log("📋 Papéis do fallback:", papeisFallback);
        
        setRoles(papeisFallback);
        setError(`Erro ao carregar do servidor: ${error.message}`);
      } finally {
        setLoading(false);
        console.log("🔚 Carregamento finalizado");
      }
    };

    carregarPapeis();
  }, [usuario?.id]);

  const handleChange = async (event) => {
    const novosRoles = event.target.value;
    
    console.log("=== HandleChange Debug ===");
    console.log("🎯 Novo valor selecionado:", novosRoles);
    console.log("📋 Roles atuais:", roles);

    // Verificar adicionados e removidos
    const adicionados = novosRoles.filter((r) => !roles.includes(r));
    const removidos = roles.filter((r) => !novosRoles.includes(r));

    console.log("➕ Papéis a adicionar:", adicionados);
    console.log("➖ Papéis a remover:", removidos);

    if (adicionados.length === 0 && removidos.length === 0) {
      console.log("ℹ️ Nenhuma mudança detectada");
      return;
    }

    try {
      setUpdating(true);
      setError(null);
      console.log("🔄 Iniciando atualização...");

      // Processar adições
      for (const role of adicionados) {
        console.log(`➕ Adicionando papel: ${role}`);
        const resultado = await adicionarPapelExtra(usuario.id, role);
        console.log("✅ Resultado da adição:", resultado);
      }

      // Processar remoções
      for (const role of removidos) {
        console.log(`➖ Removendo papel: ${role}`);
        const resultado = await removerPapelExtra(usuario.id, role);
        console.log("✅ Resultado da remoção:", resultado);
      }

      // Atualizar estado local
      setRoles(novosRoles);
      console.log("✅ Estado local atualizado para:", novosRoles);

      // Notificar componente pai
      if (onRolesUpdated) {
        console.log("📢 Notificando componente pai...");
        onRolesUpdated(novosRoles);
      }

      console.log("🎉 Atualização concluída com sucesso!");
      
    } catch (error) {
      console.error("❌ Erro ao atualizar papéis:", error);
      setError(error.message || "Erro ao atualizar papéis");
    } finally {
      setUpdating(false);
    }
  };

  console.log("🎨 Renderizando componente...");
  console.log("📊 Estado atual - roles:", roles, "loading:", loading, "error:", error);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3 }}>
        <CircularProgress size={24} />
        <Typography sx={{ ml: 2 }}>Carregando papéis...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle1" gutterBottom>
        Papéis atribuídos a {usuario.nome} (ID: {usuario.id})
      </Typography>
      
      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <FormControl fullWidth disabled={updating}>
        <InputLabel id="roles-label">Papéis Extras</InputLabel>
        <Select
          labelId="roles-label"
          multiple
          value={roles}
          onChange={handleChange}
          input={<OutlinedInput label="Papéis Extras" />}
          renderValue={(selected) => (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {selected.map((value) => {
                const option = PAPEL_OPTIONS.find((o) => o.value === value);
                return (
                  <Chip 
                    key={value} 
                    label={option ? option.label : value}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                );
              })}
            </Box>
          )}
        >
          {PAPEL_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {updating && (
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
          <CircularProgress size={16} />
          <Typography variant="body2" sx={{ ml: 1 }}>
            Atualizando papéis...
          </Typography>
        </Box>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        Debug - Papéis atuais: {JSON.stringify(roles)}
      </Typography>
    </Box>
  );
};

GerirPapeisUsuario.propTypes = {
  usuario: PropTypes.shape({
    id: PropTypes.number.isRequired,
    nome: PropTypes.string.isRequired,
    dadosAdicionais: PropTypes.object,
  }).isRequired,
  onRolesUpdated: PropTypes.func,
};

export default GerirPapeisUsuario;